import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { useMyDeliveryQuote } from "@/Apollo/hooks/product/useMyDeliveryQuote";
import { DeliveryQuoteStatus } from "@/Apollo/schema";
import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { useAuth } from "@/auth";
import { isUsLocale } from "@/i18n/config";
import { InfoPanel } from "@/ui/InfoPanel";
import { Flavor } from "@/ui/typography/Flavor";

type UsDeliveryQuoteSoldNoticeProps = {
  productId: string;
  className?: string;
};

/**
 * WHO-6509 (option a). Once a listing has sold, the delivery block and the quote in it are gone
 * from the page. A US buyer who asked for a delivery quote on it reads why here, on the sold page,
 * rather than finding their quote silently missing. The dashboard closes the quote as item sold
 * (WHO-6499); the "item sold" email (WHO-6588) is the other place they hear it.
 *
 * It shows only for a quote the dashboard closed as ITEM_SOLD (WHO-6499), which it does once the
 * listing is off sale and bought. Callers mount it where the page says the item sold, never for a
 * withdrawn listing: a quote on a listing taken off sale without a sale stays open.
 *
 * The wording holds whoever bought the item: the dashboard also closes a buyer's own quote as
 * item sold when they bought it without the quote (Andrew, 4 Nov). Telling that buyer apart is
 * for the quote emails (WHO-6588).
 *
 * Signed-in buyers on `/en-us` only, read after mount: the sold page is cached publicly and never
 * carries a buyer's quote.
 */
export const UsDeliveryQuoteSoldNotice = ({
  productId,
  className,
}: UsDeliveryQuoteSoldNoticeProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { isLoggedIn, isReady } = useAuth();
  const usMarket = isUsLocale(locale);

  const { data } = useMyDeliveryQuote({
    variables: { id: productId },
    skip: !usMarket || !isReady || !isLoggedIn,
  });

  const quote = data?.product?.myDeliveryQuote;

  /*
   * Only once the dashboard has closed the quote as item sold. A REQUESTED or QUOTED quote is
   * still open while a sale can fall through (a reserved listing returns if the payment fails),
   * so the page must not call it closed; nor a PAID one, which the buyer used themselves.
   */
  if (
    !usMarket ||
    !isLoggedIn ||
    quote?.status !== DeliveryQuoteStatus.ItemSold
  ) {
    return null;
  }

  return (
    <InfoPanel
      className={className}
      icon={<IconStateInformation className="size-5 self-start" />}
      body={
        <div
          className="flex flex-col gap-1"
          data-testid="pdp.us-delivery.sold-notice"
        >
          <Flavor size="medium" className="font-bold">
            {t("pdp:pdp-us-delivery-sold-notice-title", {
              defaultValue: "Your delivery quote is closed",
            })}
          </Flavor>
          <Flavor size="medium">
            {t("pdp:pdp-us-delivery-sold-notice-closed", {
              postalCode: quote.postalCode,
              defaultValue:
                "This item is no longer available, so your quote for delivery to {{postalCode}} has closed.",
            })}
          </Flavor>
        </div>
      }
    />
  );
};
